import {
  PointOfInterestsQuery,
  useGetUserPreferencesQuery,
  usePointOfInterestsQuery,
} from '@/__generated__/graphql';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { ALL_POIS_TAB_LABEL, LocationTag } from '@/constants';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { abyssColors, primary } from '@/theme/colors';
import {
  Columns,
  Permissions,
  PointOfInterestsQueryType,
  POIWidgets,
  TemplateTypeWithAllTab,
} from '@/types';
import AddIcon from '@mui/icons-material/Add';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AddToWorkpackDialog } from './Workpacks/Dialogs/AddToWorkpackDialog';
import { RemoveFromWorkpackDialog } from './Workpacks/Dialogs/RemoveFromWorkpackDialog';
import UpdatePoiVisibility from './UpdatePoiVisibility';
import { feedbackContainerStyles } from './styles';
import usePoiData, { RowData } from './usePoiData';
import { StatusChangeMenu } from './StatusChangeMenu';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Summary } from './Summary';
import { ColumnsSelectionDialog } from '../../ColumnsSelectionDialog';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { CustomTable } from '@/components/Analysis/shared/CustomTable/CustomTable';
import { TableSkeleton } from '../Decks/TableSkeleton';
import { useSnackBarMessage } from '../../../../../utils/useSnackBarMessage';
import { EmptyStateIcon } from '@/components/shared/EmptyStateIcon';
import useLiveSyncAllPois from '@/components/Analysis/shared/hooks/useLiveSyncAllPois';
import type { CustomFilterConfig, CustomFilterQueryVars } from '@/hooks/useCustomFilters';
import { LogWorkButton } from '@/modules/KpiTracker/components/LogWorkButton';
import { upperSnakeCase } from '@/utils';
import { usePersistFilters } from '@/components/shared/FilterAndSearchPanel/usePersistFilters';

type PointOfInterestTableProps = {
  template: TemplateTypeWithAllTab;
  selectedPointOfInterests: string[];
  handlePointOfInterestSelect: (selection: string[]) => void;
  clearSelectedPOIs: () => void;
  selectedTab: string;
  columns: Columns[];
  isVerifiedFilters: boolean[];
  isManuallyCreatedFilters?: boolean[];
  isGoverningFilters: boolean[];
  deckFilters: string[];
  unAssignedPoiFilters?: boolean[];
  mediaAttachmentFilters?: boolean[];
  accessibilityFilters: string[];
  statusFilters: string[];
  selectedPoiFilters: {
    id: string;
    value: string[];
  }[];
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<SelectedValues>>;
  selectedFilterValues: SelectedValues;
  setWorkpackIds: React.Dispatch<React.SetStateAction<string[]>>;
  workpackIds: string[];
  workpackId?: string;
  excludePoisWithWorkpack?: boolean;
  debouncedSearchTerm?: string;
  viewBy?: 'poi' | 'workpack' | 'workflow';
  /**
   * Optional array of POI IDs to fetch. When provided, only these POIs will be fetched
   * and other filters will be ignored.
   */
  poiIds?: string[];
  customFiltersConfig?: CustomFilterConfig[];
  customFilterValues?: Record<string, string[]>;
  handleCustomFilterChange?: (key: string, values: string[]) => void;
  customFilterQueryVars?: CustomFilterQueryVars;
};

export const PointOfInterestTable = ({
  template,
  selectedPointOfInterests,
  handlePointOfInterestSelect,
  clearSelectedPOIs,
  selectedTab,
  columns,
  isVerifiedFilters,
  isManuallyCreatedFilters,
  isGoverningFilters,
  unAssignedPoiFilters,
  accessibilityFilters,
  statusFilters,
  selectedPoiFilters,
  setSelectedFilterValues,
  selectedFilterValues,
  workpackId,
  excludePoisWithWorkpack,
  debouncedSearchTerm,
  mediaAttachmentFilters,
  viewBy,
  setWorkpackIds,
  workpackIds,
  poiIds,
  deckFilters,
  customFiltersConfig,
  customFilterValues,
  handleCustomFilterChange,
  customFilterQueryVars,
}: PointOfInterestTableProps) => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const isAdmin = useFeatureFlag('admin');
  const canAccessKpiTracker = useHavePermission(Permissions.UPDATE_KPI_LOGS);
  const setInsightsExplorerLoading = useSetAtom(state.insightsExplorerLoadingState);
  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    poiState.selectedPointOfInterestId
  );
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);

  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);

  const selectedDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(selectedDeckIds);

  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);

  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);
  const setSelectedDeckIds = useSetAtom(state.selectedFilteredDeckIds);
  const setSelectedAssemblyId = useSetAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);

  const [refetchUpdatedPois, setRefetchUpdatedPois] = useAtom(state.refetchUpdatedPois);

  const [savedColumns, setSavedColumns] = useState<string[]>([]);
  const { getUnitSuffix } = useUnitConversion();

  const { data: userPreferencesData } = useGetUserPreferencesQuery({ fetchPolicy: 'cache-first' });

  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);

  const [isTableSettingsOpen, setIsTableSettingsOpen] = useState(false);
  const [isPOIVisibilityModalOpen, setIsPOIVisibilityModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [pointOfInterests, setPointOfInterests] = useState<PointOfInterestsQueryType>([]);
  const [poiTotalCount, setPoiTotalCount] = useState(0);
  const [updateAll, setUpdateAll] = useState<boolean>(false);
  const [openWorkpackDialog, setOpenWorkpackDialog] = useState<boolean>(false);
  const [openRemoveFromWorkpackDialog, setOpenRemoveFromWorkpackDialog] = useState<boolean>(false);
  const [pointFetched, setPointFetched] = useState(false);
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const type =
    selectedTab === ALL_POIS_TAB_LABEL ? undefined : upperSnakeCase(template?.key);

  // Generate filter key based on viewBy and selectedTab
  const filterKey = useMemo(() => {
    const viewByPart = viewBy ? viewBy.toUpperCase() : 'POI';
    const tabPart = selectedTab ? selectedTab.toUpperCase().replace(/\s+/g, '_') : 'ALL';
    return `${viewByPart}_${tabPart}_FILTERS`;
  }, [viewBy, selectedTab]);

  const {
    savedFilterKeys,
    persistFilterKeys,
    isInitialized: isFiltersPersistenceInitialized,
  } = usePersistFilters({ filterKey });
  
  const poiSummaryChartsConfigByStructureId = useAtomValue(state.poiSummaryChartsConfigByStructureId);

  // Custom filters are now passed as props from the container

  const userPreferenceKey = useMemo(
    () => template?.name ? ((viewBy ? `${viewBy}-` : '') + template.name).toUpperCase() : undefined,
    [template?.name, viewBy]
  );

  useEffect(() => {
    if (!userPreferencesData || savedColumns.length > 0) return;

    const defaultColumns = [...columns.slice(0, 5).map(({ key }) => key), 'view3d'];
    const userPreferences = userPreferencesData?.getUserPreferences?.structure[structureId];

    // Check if user preferences exist and are valid
    const hasValidPreferences =
      userPreferences?.columns &&
      typeof userPreferences.columns === 'object' &&
      Object.keys(userPreferences.columns).length > 0;

    if (!hasValidPreferences) {
      setSavedColumns(defaultColumns);
      return;
    }
    // Use template-specific columns if available, otherwise use defaults
    const templateColumns =
      userPreferenceKey && userPreferenceKey in userPreferences.columns
        ? userPreferences.columns[userPreferenceKey]
        : defaultColumns;

    setSavedColumns(templateColumns);
  }, [savedColumns, columns, structureId, userPreferenceKey, userPreferencesData]);

  useEffect(() => {
    setApplyFilters(true);
    setPointOfInterests([]);
  }, [setApplyFilters]);

  const resetStates = useCallback(() => {
    setApplyFilters(false);
    setInsightsExplorerLoading(false);
    setRefetchUpdatedPois(false);
  }, [setApplyFilters, setInsightsExplorerLoading, setRefetchUpdatedPois]);

  const handlePoiData = useCallback(
    (data: PointOfInterestsQuery) => {
      if (data?.pointOfInterests && data?.pointOfInterestsCount?.count !== undefined) {
        setPointOfInterests(data.pointOfInterests);
        setPoiTotalCount(data.pointOfInterestsCount.count ?? 0);
        setPointFetched(true);
      }
      resetStates();
    },
    [resetStates]
  );

  const poiInputVariables = {
    input: {
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId,
      ...(poiIds
        ? { poiIds }
        : {
            selectedTemplateFilters: appliedTemplateFilters,
            selectedDeckIds: appliedDeckFilters,
            type,
            selectedPoiFilters,
            paintRegionIds: appliedPaintRegionFilters ?? [],
            workpackIds: [...new Set([...(workpackIds ?? []), ...(appliedWorkpackFilters ?? [])])],
            isVerified: isVerifiedFilters,
            isManuallyCreated: isManuallyCreatedFilters,
            isGoverning: isGoverningFilters,
            includeManuallyCreated: true,
            unAssignedPoi: unAssignedPoiFilters,
            accessibility: accessibilityFilters,
            status: statusFilters,
            deckIds: deckFilters,
            workpackId,
            excludePoisWithWorkpack,
            searchTerm: debouncedSearchTerm,
            mediaAttachment: mediaAttachmentFilters,
            ...customFilterQueryVars,
          }),
    },
    pageInfo: {
      sortBy,
      sortDir: sortDirection,
      skip: page * rowsPerPage,
      limit: rowsPerPage,
    },
  } as const;

  const { loading, refetch } = usePointOfInterestsQuery({
    variables: poiInputVariables,
    fetchPolicy: 'no-cache',
    skip: workpackId === '',
    onCompleted(data) {
      handlePoiData(data);
    },
    onError() {
      resetStates();
    },
  });

  useLiveSyncAllPois(() => {
    fetchPointOfInterests();
  });

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  const fetchPointOfInterests = useCallback(() => {
    refetch()
      .then(({ data }) => handlePoiData(data))
      .finally(() => resetStates());
  }, [resetStates, refetch, handlePoiData]);

  useEffect(() => {
    if (refetchUpdatedPois) {
      fetchPointOfInterests();
    }
  }, [fetchPointOfInterests, refetchUpdatedPois]);

  useEffect(() => {
    // Having this line run causes, the POI table to re render everytime
    // the create POI flow is triggered. That causes POIs to be fetched again
    // Commenting this for the time being, until we find the root cause (or not)
    // setApplyFilters(true);
    if (workpackId === '') return;
    fetchPointOfInterests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortBy,
    sortDirection,
    page,
    rowsPerPage,
    editPointOfInterest.state,
    workpackId,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(selectedDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
      setPage(0);
    }
  }, [
    applyFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedTemplateFilters,
    selectedWorkpackIds,
  ]);

  const hiddenColumns = useMemo(() => {
    return columns
      .filter((column) => !savedColumns.includes(column.key))
      .map((column) => column.key);
  }, [columns, savedColumns]);

  const { rows } = usePoiData({
    pointOfInterests,
    columns,
    hiddenColumns,
  });

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedFilterValues(filteredSelectedValues);
      setApplyFilters(true);
    },
    [setApplyFilters, setSelectedFilterValues]
  );

  const fieldsWithFilter = useMemo(() => {
    return template?.fields.filter((field) => field.options?.length > 0 && field.displayInQuery);
  }, [template]);

  const togglePOIVisibilityModalOpen = useCallback(
    (status: boolean) => () => {
      setIsPOIVisibilityModalOpen(status);
    },
    []
  );

  const handlePoiSelected = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, poi: RowData) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      setSelectedPointOfInterestId(
        poi.id.toString() === selectedPointOfInterestId ? undefined : poi.id.toString()
      );
      setSelectedAssemblyId(poi.assemblyId);
      setSelectedAssemblyName(poi.assembly);
    },
    [
      selectedPointOfInterestId,
      setSelectedAssemblyId,
      setSelectedAssemblyName,
      setSelectedPointOfInterestId,
    ]
  );

  useEffect(() => {
    if (selectedPointOfInterests?.length === rows?.length) {
      setUpdateAll(true);
    }

    if (selectedPointOfInterests?.length === 0) {
      setUpdateAll(false);
    } else {
      setUpdateAll(false);
    }
  }, [rows?.length, selectedPointOfInterests?.length]);

  const filtersApplied = useMemo(() => {
    return (
      selectedPoiFilters?.length > 0 ||
      selectedTemplateFilters?.length > 0 ||
      isVerifiedFilters?.length > 0 ||
      isGoverningFilters?.length > 0 ||
      accessibilityFilters?.length > 0 ||
      statusFilters?.length > 0
    );
  }, [
    selectedPoiFilters?.length,
    selectedTemplateFilters?.length,
    isVerifiedFilters?.length,
    isGoverningFilters?.length,
    accessibilityFilters?.length,
    statusFilters?.length,
    customFilterQueryVars?.length,
  ]);

  const clearQuery = useCallback(() => {
    setSelectedTemplateFilters([]);
    setSelectedDeckIds([]);
    setSelectedPaintRegionIds([]);
    setWorkpackIds([]);
    setSelectedFilterValues({});
    setApplyFilters(true);
  }, [
    setApplyFilters,
    setSelectedDeckIds,
    setSelectedFilterValues,
    setSelectedPaintRegionIds,
    setSelectedTemplateFilters,
    setWorkpackIds,
  ]);
  const feedbackCircleText = useMemo(() => {
    return selectedTab === ALL_POIS_TAB_LABEL
      ? 'No Points of Interest found for the selected category and filters.'
      : `No ${selectedTab.toLowerCase()} POIs found  for the selected category and filters.`;
  }, [selectedTab]);

  const dialogColumns = useMemo(() => {
    const updatedColumns = [...savedColumns];

    if (!updatedColumns.includes('assembly')) {
      updatedColumns.push('assembly');
    }

    return columns.map(({ key, columnName }) => ({
      key,
      templateFieldKey: key,
      name: columnName ?? key.toUpperCase(),
      isSelected: updatedColumns.includes(key),
    }));
  }, [columns, savedColumns]);

  const visibleColumns = useMemo(() => {
    return columns
      .filter((column) => savedColumns.includes(column.key))
      .map((column) => {
        const { displayUnit, unit, columnName, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({ unit: displayUnit ?? unit, field: templateFieldKey });
        const updatedName = unitSuffix ? `${columnName} (${unitSuffix})` : columnName;
        return { ...column, columnName: updatedName };
      });
  }, [columns, getUnitSuffix, savedColumns]);

  const handleApplyColumns = useCallback((selectedColumnsKeys: string[]) => {
    setSavedColumns(selectedColumnsKeys);
  }, []);

  const clearQueryComponent = useMemo(() => {
    return (
      <>
        {filtersApplied && workpackId && (
          <Stack direction="row" gap={2} alignItems="center" pb={2}>
            <InfoIcon sx={{ color: abyssColors.primary[300] }} />
            <Typography
              noWrap
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: abyssColors.primary[300],
                minWidth: '6rem',
              }}
            >
              Results shown in the table below are based on your applied query or filters
            </Typography>
            <Button
              id="poi-table-clear-query"
              variant="text"
              startIcon={<CancelIcon />}
              sx={{
                ml: 1,
                height: 1,
                color: primary.darkBlue,
                fontWeight: 'bold',
                textTransform: 'none',
              }}
              onClick={clearQuery}
            >
              Clear Query
            </Button>
          </Stack>
        )}
      </>
    );
  }, [clearQuery, filtersApplied, workpackId]);

  const handleSort = useCallback(
    (sortByField: string) => {
      const isAsc = sortBy === sortByField && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(sortByField);
    },
    [setSortDirection, sortBy, sortDirection]
  );
  const pointOfInterestsTable = useMemo(() => {
    if (pointOfInterests.length > 0 && !loading && pointFetched) {
      return (
        <CustomTable
          columns={visibleColumns}
          data={rows ?? []}
          totalCount={poiTotalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
          handleSort={handleSort}
          onRowSelect={handlePointOfInterestSelect}
          selectedRowIds={selectedPointOfInterests}
          onRowClick={handlePoiSelected}
          showGearIcon={false}
          selectedRow={selectedPointOfInterestId}
          sortBy={sortBy}
          sortDirection={sortDirection}
          name="poi"
        />
      );
    } else if (pointOfInterests.length === 0 && !loading && pointFetched) {
      return (
        <Box sx={feedbackContainerStyles}>
          <FeedbackCircle text={feedbackCircleText} icon={<EmptyStateIcon icon={selectedTab} poiTemplateKey={template.key} />} />
        </Box>
      );
    } else {
      return <TableSkeleton />;
    }
  }, [
    loading,
    pointOfInterests.length,
    visibleColumns,
    rows,
    poiTotalCount,
    page,
    template.key,
    rowsPerPage,
    handlePointOfInterestSelect,
    selectedPointOfInterests,
    handlePoiSelected,
    selectedPointOfInterestId,
    feedbackCircleText,
    selectedTab,
    pointFetched,
    sortBy,
    sortDirection,
    handleSort,
    customFilterQueryVars,
  ]);

  const noPoisSelected = selectedPointOfInterests.length === 0;

  const actionMessageHandler = () => {
    if (noPoisSelected) {
      showSnackBar(
        'Please select at least one point of interest to enable actions on them.',
        'warning'
      );
    }
  };

  const queryInput = useMemo(
    () => ({
      selectedTemplateFilters: appliedTemplateFilters,
      assemblyTemplateId: selectedAssemblyTemplateId,
      selectedPoiFilters,
      type,
      structureIds: structureId ? [structureId] : [],
      paintRegionIds: appliedPaintRegionFilters ?? [],
      isVerified: isVerifiedFilters,
      isGoverning: isGoverningFilters,
      isManuallyCreated: isManuallyCreatedFilters,
      unAssignedPoi: unAssignedPoiFilters,
      accessibility: accessibilityFilters,
      status: statusFilters,
      deckIds: deckFilters,
      searchTerm: debouncedSearchTerm,
      mediaAttachment: mediaAttachmentFilters,
      includeManuallyCreated: true,
      selectedDeckIds: appliedDeckFilters,
      workpackIds: [...new Set([...(workpackIds ?? []), ...(appliedWorkpackFilters ?? [])])],
      ...customFilterQueryVars,
    }),
    [
      selectedAssemblyTemplateId,
      appliedTemplateFilters,
      selectedPoiFilters,
      type,
      structureId,
      appliedPaintRegionFilters,
      isVerifiedFilters,
      isGoverningFilters,
      isManuallyCreatedFilters,
      unAssignedPoiFilters,
      accessibilityFilters,
      statusFilters,
      debouncedSearchTerm,
      mediaAttachmentFilters,
      appliedDeckFilters,
      appliedWorkpackFilters,
      workpackIds,
      customFilterQueryVars,
      deckFilters,
    ]
  );

  const visibleWidgets = useMemo(() => {
    const commonWidgets = [POIWidgets.Status, POIWidgets.DueDate];
    const templateWidgets = new Set([
      POIWidgets.InspectionPriority,
      POIWidgets.PaintDamageAlarmLevels,
    ]);

    const visibleTemplateWidgets = template.fields
      .filter((field) => templateWidgets.has(field.name as POIWidgets))
      .map((field) => field.name) as POIWidgets[];

    const defaultWidgets = [...commonWidgets, ...visibleTemplateWidgets];

    const poiWidgetOrder = Object.values(POIWidgets);
    const sortByEnumOrder = (widgets: POIWidgets[]) =>
      widgets.sort((a, b) => poiWidgetOrder.indexOf(a) - poiWidgetOrder.indexOf(b));

    const configKey = selectedTab === ALL_POIS_TAB_LABEL ? 'ALL' : upperSnakeCase(template?.key ?? '');
    const perStructureConfig = structureId ? poiSummaryChartsConfigByStructureId[structureId] : undefined;
    const configured = configKey ? perStructureConfig?.[configKey] : undefined;

    if (Array.isArray(configured)) {
      const validWidgetValues = new Set(Object.values(POIWidgets));
      const filtered = configured.filter((w): w is POIWidgets => validWidgetValues.has(w as POIWidgets));
      return sortByEnumOrder(filtered);
    }

    return sortByEnumOrder(defaultWidgets);
  }, [poiSummaryChartsConfigByStructureId, selectedTab, structureId, template.fields, template?.key]);

  return (
    <Box data-testid="point-of-interest-table" sx={{ mb: 1, pt: workpackId ? 3 : 0 }}>
      {viewBy === 'poi' && <Summary queryInput={queryInput} visibleWidgets={visibleWidgets} />}
      <>
        <FilterAndSearchPanel
          selectedValues={{ ...selectedFilterValues, ...customFilterValues }}
          setSelectedValues={setSelectedFilterValues}
          templateFields={fieldsWithFilter || []}
          handleSelectedChange={handleSelectedFilterValuesChange}
          showPaintRegions
          customFiltersConfig={customFiltersConfig}
          customFilterValues={customFilterValues}
          handleCustomFilterChange={handleCustomFilterChange}
          location={LocationTag.InsightsPoiTable}
          savedFilterKeys={savedFilterKeys}
          onFilterKeysChange={persistFilterKeys}
          isFiltersPersistenceInitialized={isFiltersPersistenceInitialized}
          filterPersistenceKey={filterKey}
          endAdornment={
            <IconButton onClick={() => setIsTableSettingsOpen(true)} data-testid="poi-table-config">
              <AbyssIcon iconName="gear" />
            </IconButton>
          }
        />
        <>
          <Stack direction="row" alignItems="center" mb={3} gap={1.5}>
            <Typography
              sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: abyssColors.primary[400] }}
            >
              {updateAll ? poiTotalCount : selectedPointOfInterests?.length} POI Selected
            </Typography>
            <Stack onClick={actionMessageHandler} direction="row" alignItems="center" gap={1.5}>
              {viewBy !== 'workflow' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={workpackId ? <ArrowOutwardIcon /> : <AddIcon />}
                  onClick={() => setOpenWorkpackDialog(true)}
                  disabled={noPoisSelected}
                >
                  {workpackId ? 'Move to another workpack' : 'Add to workpack'}
                </Button>
              )}

              {/* Dont show when POI table is active */}
              {workpackId && (
                <LogWorkButton
                  disabled={!workpackId || !canAccessKpiTracker}
                  selectedWorkpackId={workpackId}
                />
              )}
              {workpackId && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={() => setOpenRemoveFromWorkpackDialog(true)}
                  disabled={noPoisSelected}
                >
                  Remove from workpack
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={togglePOIVisibilityModalOpen(true)}
                  disabled={noPoisSelected}
                >
                  POI visibility
                </Button>
              )}
              <StatusChangeMenu
                selectedPointOfInterests={selectedPointOfInterests}
                refetchPointOfInterests={fetchPointOfInterests}
                disabled={noPoisSelected}
              />
            </Stack>
          </Stack>
          <UpdatePoiVisibility
            open={isPOIVisibilityModalOpen}
            handleClose={togglePOIVisibilityModalOpen(false)}
            title="Point of Interest Visibility"
            selectedPointOfInterestIds={selectedPointOfInterests}
            clearSelectedPOIs={clearSelectedPOIs}
            isBulkUpdate={updateAll}
            selectedPoiFilters={selectedPoiFilters}
            type={type ?? ''}
          />
        </>
        {clearQueryComponent}
        {pointOfInterestsTable}
      </>
      <ColumnsSelectionDialog
        open={isTableSettingsOpen}
        columns={dialogColumns}
        heading="Enable / Disable the columns"
        subHeading="Select the columns you want to display:"
        applyButtonText="Apply"
        userPreferenceKey={userPreferenceKey}
        userPreferenceType="columns"
        onApply={handleApplyColumns}
        onClose={() => setIsTableSettingsOpen(false)}
      />

      {openWorkpackDialog && (
        <AddToWorkpackDialog
          isOpen={openWorkpackDialog}
          handleWorkpackDialogClose={setOpenWorkpackDialog}
          title="Add point of interests to workpack"
          selectedPointOfInterestIds={selectedPointOfInterests}
          filtersApplied={filtersApplied}
          refetchPointOfInterests={fetchPointOfInterests}
          viewBy={viewBy}
        />
      )}
      <RemoveFromWorkpackDialog
        isOpen={openRemoveFromWorkpackDialog}
        handleWorkpackDialogClose={setOpenRemoveFromWorkpackDialog}
        title="Remove POIs from workpack"
        workpackId={workpackId ?? ''}
        selectedPointOfInterestIds={selectedPointOfInterests}
      />
    </Box>
  );
};
